import * as React from "react";
import { createIntl, createIntlCache, RawIntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { createRoot } from "react-dom/client";
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import thunk from "redux-thunk";

import "./gtm";
import initializeSentry from "./initializeSentry";
import messages_en from "./translations/en.json";
import messages_no from "./translations/nb.json";
import "promise/polyfill";
import "whatwg-fetch";

import { injectGlobals } from "styles/styles";
import "normalize.css";

import App from "./containers/App";
import api from "./helpers/middleware/api";
import stores from "stores";

initializeSentry();
injectGlobals();

const rootReducer = combineReducers({ ...stores });

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleWare) => getDefaultMiddleWare().prepend(api, thunk),
});

const cache = createIntlCache();

const nbIntl = createIntl(
  {
    locale: "nb",
    messages: messages_no,
    defaultLocale: "nb",
  },
  cache,
);

const enIntl = createIntl(
  {
    locale: "en",
    messages: messages_en,
    defaultLocale: "en",
  },
  cache,
);

export const languages = {
  en: {
    intl: enIntl,
    datePickerFormat: "MM/dd/yyyy",
    label: "English",
    shortLabel: "En",
  },
  nb: {
    intl: nbIntl,
    datePickerFormat: "dd.MM.yyyy",
    label: "Norsk",
    shortLabel: "No",
  },
};

export type Language = "nb" | "en";
const availableLanguages = Object.keys(languages);
const LANGUAGE_KEY = "hoopla-language";

export const messages =
  process.env.REACT_APP_REGION === "NO"
    ? {
        nb: messages_no,
        en: messages_en,
      }
    : {
        en: messages_en,
      };

const getLanguage = (): string => {
  return (
    localStorage.getItem(LANGUAGE_KEY) || (process.env.REACT_APP_REGION === "NO" ? "nb" : "en")
  );
};

export const setLanguage = (language: Language) => {
  localStorage.setItem(LANGUAGE_KEY, language);
};

export const LangContext = React.createContext(null);

const Application: React.FC = () => {
  const [lang, setLang] = React.useState<string>(getLanguage()); // keeps state of lang in App
  function onChangeLang(lang: Language) {
    setLanguage(lang);
    setLang(lang);
  }

  return (
    <RawIntlProvider
      value={
        availableLanguages.includes(lang)
          ? languages[lang].intl
          : languages[availableLanguages[0]].intl
      }
    >
      <LangContext.Provider value={{ lang, setLang: onChangeLang }}>
        <App />
      </LangContext.Provider>
    </RawIntlProvider>
  );
};

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <Provider store={store}>
    <Application />
  </Provider>,
);
